import React from "react";
import { Box, Typography } from "@material-ui/core";
import "./styles.scss";
import clsx from "clsx";
import {
  Icon,
  useDeviceTypes,
  DefaultImageBanner,
  GENERIC_HOTEL_PROMOTION_OFFER_IMAGE,
  VibeCallToActionSection,
} from "halifax";
import { useHistory } from "react-router";
import {
  appendIsTravelAppParam,
  COMPLIMENTARY_NIGHT_PROMOTION_TITLE,
  COMPLIMENTARY_NIGHT_PROMOTION_DESCRIPTION,
  COMPLIMENTARY_NIGHT_PROMOTION_BUTTON_TEXT,
  PATH_PREMIUM_OFFERS_SHOP,
  useGetVibesQuery,
  useIsTravelApp,
} from "@capone/common";
import {
  AVAILABLE,
} from "@capone/experiments";
import {
  StayTypesEnum,
  TravelWalletOffer,
  VIEWED_PREMIUM_OFFER_PAGE,
  ViewedCompNightBannerEntryPoint,
  ViewedPremiumOfferPageProperties,
} from "redmond";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  useExperiments,
  getExperimentVariant,
  LC_GATING_EXPERIMENT,
  PREMIER_COLLECTION_EXPERIMENT,
} from "../../../../context/experiments";
import { LuxQ2VROfferBanner } from "../LuxQ2VROfferBanner/component";

import * as textConstants from "./textConstants";
import { BenefitItem, BenefitConfig, PerkItem } from "./types";
import {
  PREMIER_BENEFITS_CONFIG,
  getLifestyleBenefitsConfig,
  PERKS_CONFIG,
} from "./benefitConfigs";

export interface IPremierCollectionLandingPageBenefitsProps {
  isVentureX?: boolean;
  stayType?: StayTypesEnum;
  travelWalletOffers?: TravelWalletOffer[];
}

const renderBenefitsList = (
  items: BenefitItem[],
  className: string,
  showTitle: boolean,
): JSX.Element => (
  <>
    {showTitle && (
      <Typography className="benefits-row-title">
        {textConstants.collectionBenefitsTitle}
      </Typography>
    )}
    {items.map((item) => (
      <Box key={item.text} className={className}>
        {item.icon && (
          <Icon
            name={item.icon}
            className={item.iconClassName || "benefit-icon"}
          />
        )}
        <Typography className="benefit-text">{item.text}</Typography>
      </Box>
    ))}
  </>
);

const getBenefitsItems = (
  config: BenefitConfig,
  isVacationRentals: boolean,
  isMobile: boolean,
): BenefitItem[] => {
  let scenario: keyof BenefitConfig;
  if (isMobile) {
    scenario = "mobile";
  } else if (isVacationRentals) {
    scenario = "vacationRentals";
  } else {
    scenario = "hotels";
  }

  return config[scenario];
};

// Simplified perks renderer
const renderPerks = (isVacationRentals: boolean): JSX.Element => {
  const perks: PerkItem[] = isVacationRentals
    ? PERKS_CONFIG.vacationRentals
    : PERKS_CONFIG.hotels;

  return (
    <>
      {perks.map((perk) => (
        <Box key={perk.text} className="perk">
          <Icon name={perk.icon} className={perk.iconClassName} />
          <Typography className="perk-detail" variant="h6">
            {perk.text}
          </Typography>
        </Box>
      ))}
    </>
  );
};

export const PremierCollectionLandingPageBenefits = (
  props: IPremierCollectionLandingPageBenefitsProps,
): JSX.Element => {
  const { isVentureX = false, stayType, travelWalletOffers = [] } = props;

  const { data: vibesResponse } = useGetVibesQuery();
  const vibes = vibesResponse?.vibes || [];

  const { matchesMobile } = useDeviceTypes();
  const isMobile = matchesMobile;
  const isVacationRentals = stayType === StayTypesEnum.VacationRentals;
  const history = useHistory();
  const { isTravelApp } = useIsTravelApp();
  const expState = useExperiments();
  const isVibesXpEnabled =
    getExperimentVariant(
      expState.experiments,
      LC_GATING_EXPERIMENT,
    ) === AVAILABLE;
  const showsPremierCollection =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT,
    ) === AVAILABLE;
  const premierItems = getBenefitsItems(
    PREMIER_BENEFITS_CONFIG,
    isVacationRentals,
    isMobile,
  );
  const lifestyleItems = getBenefitsItems(
    getLifestyleBenefitsConfig(isVentureX),
    isVacationRentals,
    isMobile,
  );

  return (
    <Box
      className={clsx("premier-collection-landing-page-benefits-root", {
        mobile: isMobile,
      })}
    >
      <Box className="benefits-header">
        <Typography className="benefits-section-title" variant="h1">
          {textConstants.getBenefitsTitle(isVentureX)}
        </Typography>
        <Typography className="benefits-section-subtitle" variant="h5">
          {textConstants.getBenefitsSubtitle(isVentureX, stayType, isMobile)}
        </Typography>
      </Box>
      <Box
        className={clsx("benefits-content-containers-wrapper", {
          "single-collection": !isVentureX,
        })}
      >
        {isVentureX && (
          <Box className="premier-collection-benefits-content-container">
            <Box className="benefits-container">
              <Box className="collection-header">
                <Typography className="collection-title">
                  {textConstants.premierCollectionTitle}
                </Typography>
                <Typography className="collection-subtitle">
                  {textConstants.getCollectionSubtitle(
                    true,
                    isVacationRentals,
                    isMobile,
                  )}
                </Typography>
              </Box>
              <Box
                className={clsx("premier-hotel-benefits-row", {
                  "two-benefits": !isMobile && premierItems.length === 2,
                })}
              >
                {renderBenefitsList(
                  premierItems,
                  "premier-benefit-item",
                  isMobile,
                )}
              </Box>
            </Box>
          </Box>
        )}

        <Box className="lifestyle-collection-benefits-content-container">
          <Box className="benefits-container">
            <Box className="collection-header">
              <Typography className="collection-title">
                {textConstants.lifestyleCollectionTitle}
              </Typography>
              <Typography className="collection-subtitle">
                {textConstants.getCollectionSubtitle(
                  false,
                  isVacationRentals,
                  isMobile,
                )}
              </Typography>
            </Box>
            <Box
              className={clsx("lifestyle-hotel-benefits-row", {
                "two-benefits": !isMobile && lifestyleItems.length === 2,
              })}
            >
              {renderBenefitsList(
                lifestyleItems,
                "lifestyle-benefit-item",
                isMobile,
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box className="perks-container">
        <Typography className="perks-subtitle" variant="body1">
          {textConstants.getPerksSubtitle(isVentureX)}
        </Typography>
        <Box className="perks-content">{renderPerks(isVacationRentals)}</Box>
      </Box>

      {!isVacationRentals && isVibesXpEnabled && (
        <VibeCallToActionSection
          ctas={vibes
            .filter((vibe) => vibe.basicVibe?.ctaHomepage)
            .map((vibe) => ({
              title: vibe.basicVibe.ctaHomepage.title,
              subtitle: vibe.basicVibe.ctaHomepage.subtitle,
              media: vibe.basicVibe.ctaHomepage.media,
              vibeName: vibe.vibeName,
            }))}
          title={textConstants.ctaSectionTitle}
          subtitle={textConstants.ctaSectionSubtitle}
          onButtonClick={(vibeName) => {
            trackEvent({
              eventName: "clicked_vibe_entrypoint",
              properties: {
                vibe_name: vibeName,
                screen: "premium_stays_landing",
                section: "destinations",
                funnel: "premier_collection",
              },
            });
            history.push(
              `/premium-stays/vibes?vibe=${encodeURIComponent(vibeName)}`,
            );
          }}
        />
      )}

      {!isVacationRentals && (
        <Box className="luxury-promotion-banner-container">
          <DefaultImageBanner
            imageUrl={GENERIC_HOTEL_PROMOTION_OFFER_IMAGE}
            title={COMPLIMENTARY_NIGHT_PROMOTION_TITLE}
            description={COMPLIMENTARY_NIGHT_PROMOTION_DESCRIPTION(
              showsPremierCollection,
            )}
            buttonText={COMPLIMENTARY_NIGHT_PROMOTION_BUTTON_TEXT}
            buttonAction={() => {
              trackEvent({
                eventName: VIEWED_PREMIUM_OFFER_PAGE,
                properties: {
                  entry_point:
                    ViewedCompNightBannerEntryPoint.PREMIUM_STAYS_LANDING,
                } as ViewedPremiumOfferPageProperties,
              });
              history.push(
                appendIsTravelAppParam(PATH_PREMIUM_OFFERS_SHOP, isTravelApp),
              );
            }}
          />
        </Box>
      )}

      {(isMobile || isVacationRentals) && (
        <LuxQ2VROfferBanner travelWalletOffers={travelWalletOffers} />
      )}
    </Box>
  );
};
